import { Manager } from "@twilio/flex-ui";
import axios from "axios";
import { toCamelCase, toSnakeCase } from "case-converter";
import Debug from "debug";

import UpscopeError from "../../utils/upscopeError";

import { cacheVisitor, fetchFromCache } from "./cache";
import { UpscopeEnv, upscopeURL } from "./utils";

import {
  UpscopeApiErrorResponse,
  UpscopeLookupCodeRequest,
  UpscopeRequest,
  UpscopeSearchMethod,
  UpscopeSuccessResponse,
} from "~integration/types/integration-objects";

const debug = Debug("upscope:upscope_client");

type RequestAuthResponse = {
  integrationToken: string;
};

export async function requestUpscope<T extends UpscopeSearchMethod>({
  request,
  searchMethod,
  integrationToken,
  upscopeEnv,
}: {
  searchMethod: T;
  request: (T extends "from_visitor_details" ? UpscopeRequest : UpscopeLookupCodeRequest) & { conversationId?: string };
  integrationToken: string;
  upscopeEnv: UpscopeEnv;
}): Promise<UpscopeSuccessResponse> {
  const { conversationId } = request;

  const visitorFromCache = fetchFromCache<UpscopeSuccessResponse>(conversationId);
  if (visitorFromCache) return visitorFromCache;

  try {
    const { data } = await axios.post<UpscopeSuccessResponse>(
      `${upscopeURL(upscopeEnv)}/twilioFlex/${searchMethod}`,
      toSnakeCase(request),
      {
        headers: {
          "X-Api-Key": integrationToken,
          "Content-Type": "application/json",
        },
      },
    );

    const response = toCamelCase(data) as UpscopeSuccessResponse;
    if (conversationId) cacheVisitor(conversationId, response);

    return response;
  } catch (error: any) {
    debug("Error with request: %o", error);
    let errorResponse: UpscopeApiErrorResponse | undefined;

    if (axios.isAxiosError(error)) {
      if (error.response && typeof error.response.data !== "undefined")
        errorResponse = toCamelCase(error.response.data);
    } else {
      errorResponse = { error: "unknown", status: "error" };
    }

    const customError = new UpscopeError<UpscopeApiErrorResponse>({
      message: error.message,
      status: error.status,
      response: errorResponse,
    });
    debug("Upscope search error: %o", customError);
    throw customError;
  }
}

export async function requestAuth(authUrl: string): Promise<RequestAuthResponse> {
  const twilioAuthToken = Manager.getInstance().user.token;

  try {
    const { data } = await axios.post<RequestAuthResponse>(authUrl, {
      Token: twilioAuthToken,
    });
    const response = toCamelCase(data) as RequestAuthResponse;

    return response;
  } catch (error: any) {
    debug("Error with request auth (%O)", error);
    const serverError = new Error(error.response?.data?.message ?? "Unknown request auth error") as Error & {
      statusCode?: number;
      response: Record<string, any>;
    };

    serverError.statusCode = error.response?.status;
    serverError.response = error.response;

    throw serverError;
  }
}
